<template>
  <div class="accessibility-widget" :style="panelStyles">
    <!-- Botão flutuante de acessibilidade -->
    <button
      ref="accessibilityButton"
      class="accessibility-toggle-btn"
      :class="{ 'active': isPanelOpen }"
      @click="togglePanel"
      :aria-label="isPanelOpen ? 'Fechar painel de acessibilidade' : 'Abrir painel de acessibilidade'"
      :aria-expanded="isPanelOpen"
      tabindex="0"
    >
      <i class="feather icon-eye" aria-hidden="true"></i>
    </button>

    <!-- Painel de controles de acessibilidade -->
    <transition name="slide-up">
      <div
        v-if="isPanelOpen"
        class="accessibility-panel"
        role="dialog"
        aria-labelledby="accessibility-panel-title"
        :style="panelStyles"
      >
        <div class="panel-header">
          <h3 id="accessibility-panel-title" class="panel-title">
            <i class="feather icon-eye mr-2" aria-hidden="true"></i>
            Acessibilidade
          </h3>
          <button
            class="close-btn"
            @click="closePanel"
            aria-label="Fechar painel de acessibilidade"
            tabindex="0"
          >
            <i class="feather icon-x" aria-hidden="true"></i>
          </button>
        </div>

        <div class="panel-content">
          <!-- Controles de Fonte -->
          <div class="control-group">
            <h4 class="control-title">
              <i class="feather icon-type mr-2" aria-hidden="true"></i>
              Tamanho da Fonte
            </h4>
            <div class="control-buttons">
              <button
                class="control-btn"
                @click="decreaseFontSize"
                :disabled="fontSize <= minFontSize"
                aria-label="Diminuir tamanho da fonte"
                tabindex="0"
              >
                <i class="feather icon-minus" aria-hidden="true"></i>
                <span>A-</span>
              </button>
              <span
                class="font-size-display"
                aria-live="polite"
                :class="{ 'font-size-changed': fontSizeChanged }"
              >{{ fontSize }}%</span>
              <button
                class="control-btn"
                @click="increaseFontSize"
                :disabled="fontSize >= maxFontSize"
                aria-label="Aumentar tamanho da fonte"
                tabindex="0"
              >
                <i class="feather icon-plus" aria-hidden="true"></i>
                <span>A+</span>
              </button>
            </div>
          </div>

          <!-- Controle de Contraste -->
          <div class="control-group">
            <h4 class="control-title">
              <i class="feather icon-sun mr-2" aria-hidden="true"></i>
              Contraste
            </h4>
            <div class="control-buttons">
              <button
                class="control-btn toggle-btn"
                :class="{ 'active': isHighContrast }"
                @click="toggleHighContrast"
                :aria-label="isHighContrast ? 'Desativar alto contraste' : 'Ativar alto contraste'"
                :aria-pressed="isHighContrast"
                tabindex="0"
              >
                <i class="feather icon-moon" aria-hidden="true"></i>
                <span>Alto Contraste</span>
              </button>
            </div>
          </div>

          <!-- Controles de Leitura -->
          <div class="control-group">
            <h4 class="control-title">
              <i class="feather icon-volume-2 mr-2" aria-hidden="true"></i>
              Leitura de Texto
            </h4>
            <div class="control-buttons">
              <button
                class="control-btn toggle-btn"
                :class="{ 'active': isTextToSpeechEnabled }"
                @click="toggleTextToSpeech"
                :aria-label="isTextToSpeechEnabled ? 'Desativar leitura de texto' : 'Ativar leitura de texto'"
                :aria-pressed="isTextToSpeechEnabled"
                tabindex="0"
              >
                <i class="feather icon-headphones" aria-hidden="true"></i>
                <span>Narração</span>
              </button>
              <button
                v-if="isTextToSpeechEnabled"
                class="control-btn"
                @click="readPageContent"
                aria-label="Ler conteúdo da página"
                tabindex="0"
              >
                <i class="feather icon-play" aria-hidden="true"></i>
                <span>Ler Página</span>
              </button>
            </div>
          </div>

          <!-- Controles de Navegação -->
          <div class="control-group">
            <h4 class="control-title">
              <i class="feather icon-navigation mr-2" aria-hidden="true"></i>
              Navegação
            </h4>
            <div class="control-buttons">
              <button
                class="control-btn toggle-btn"
                :class="{ 'active': isKeyboardNavigationEnhanced }"
                @click="toggleKeyboardNavigation"
                :aria-label="isKeyboardNavigationEnhanced ? 'Desativar navegação aprimorada' : 'Ativar navegação aprimorada'"
                :aria-pressed="isKeyboardNavigationEnhanced"
                tabindex="0"
              >
                <i class="feather icon-command" aria-hidden="true"></i>
                <span>Navegação++</span>
              </button>
            </div>
          </div>

          <!-- Modo de Leitura -->
          <div class="control-group">
            <h4 class="control-title">
              <i class="feather icon-book-open mr-2" aria-hidden="true"></i>
              Interface
            </h4>
            <div class="control-buttons">
              <button
                class="control-btn toggle-btn"
                :class="{ 'active': isReadingMode }"
                @click="toggleReadingMode"
                :aria-label="isReadingMode ? 'Desativar modo leitura' : 'Ativar modo leitura'"
                :aria-pressed="isReadingMode"
                tabindex="0"
              >
                <i class="feather icon-eye-off" aria-hidden="true"></i>
                <span>Modo Leitura</span>
              </button>
            </div>
          </div>

          <!-- Reset -->
          <div class="control-group">
            <button
              class="control-btn reset-btn"
              @click="resetAccessibilitySettings"
              aria-label="Restaurar configurações padrão de acessibilidade"
              tabindex="0"
            >
              <i class="feather icon-refresh-cw mr-2" aria-hidden="true"></i>
              Restaurar Padrão
            </button>
          </div>

          <!-- Atalhos de Teclado -->
          <div class="control-group">
            <h4 class="control-title">
              <i class="feather icon-keyboard mr-2" aria-hidden="true"></i>
              Atalhos de Teclado
            </h4>
            <div class="shortcuts-info">
              <small>
                <strong>Alt + A:</strong> Abrir/Fechar Painel<br>
                <strong>Alt + +:</strong> Aumentar Fonte<br>
                <strong>Alt + -:</strong> Diminuir Fonte<br>
                <strong>Alt + C:</strong> Alto Contraste<br>
                <strong>Alt + S:</strong> Narração<br>
                <strong>ESC:</strong> Fechar Painel
              </small>
            </div>
          </div>

          <!-- Indicador de Status -->
          <div v-if="statusMessage" class="status-message" role="status" aria-live="polite">
            {{ statusMessage }}
          </div>
        </div>
      </div>
    </transition>

    <!-- Overlay para fechar painel -->
    <div
      v-if="isPanelOpen"
      class="accessibility-overlay"
      @click="closePanel"
      aria-hidden="true"
    ></div>
  </div>
</template>

<script>
export default {
  name: 'AccessibilityPanel',
  props: {
    primaryColor: {
      type: String,
      default: '#156440'
    }
  },
  data() {
    return {
      isPanelOpen: false,
      fontSize: 100,
      minFontSize: 80,
      maxFontSize: 150,
      isHighContrast: false,
      isTextToSpeechEnabled: false,
      isKeyboardNavigationEnhanced: false,
      isReadingMode: false,
      statusMessage: '',
      speechSynthesis: null,
      currentSpeech: null,
      fontSizeChanged: false
    }
  },
  computed: {
    panelStyles() {
      return {
        '--primary-color': this.primaryColor,
        '--primary-color-light': this.lightenColor(this.primaryColor, 0.1),
        '--primary-color-dark': this.darkenColor(this.primaryColor, 0.1)
      }
    }
  },
  mounted() {
    this.loadAccessibilitySettings();
    this.initializeSpeechSynthesis();
    this.setupKeyboardListeners();
    this.applyAllSettings();

    // Debug para verificar se a cor está sendo aplicada
    if (process.env.NODE_ENV === 'development') {
      console.log('🎨 AccessibilityPanel - Cor primária recebida:', this.primaryColor);
      console.log('🎨 CSS Variables aplicadas:', this.panelStyles);
    }
  },
  beforeDestroy() {
    this.removeKeyboardListeners();
    this.stopSpeech();
  },
  methods: {
    // ========================================
    // CONTROLE DO PAINEL
    // ========================================
    togglePanel() {
      this.isPanelOpen = !this.isPanelOpen;
      if (this.isPanelOpen) {
        this.$nextTick(() => {
          this.focusFirstControl();
        });
      }
    },

    closePanel() {
      this.isPanelOpen = false;
      this.$nextTick(() => {
        this.$refs.accessibilityButton?.focus();
      });
    },

    focusFirstControl() {
      const firstButton = this.$el.querySelector('.accessibility-panel .control-btn');
      if (firstButton) {
        firstButton.focus();
      }
    },

    // ========================================
    // CONTROLES DE FONTE
    // ========================================
    increaseFontSize() {
      if (this.fontSize < this.maxFontSize) {
        this.fontSize += 2;
        this.applyFontSize();
        this.saveSettings();
        this.showStatus(`Fonte aumentada para ${this.fontSize}%`);
        this.debugFontChange('increase');
      }
    },

    decreaseFontSize() {
      if (this.fontSize > this.minFontSize) {
        this.fontSize -= 2;
        this.applyFontSize();
        this.saveSettings();
        this.showStatus(`Fonte reduzida para ${this.fontSize}%`);
        this.debugFontChange('decrease');
      }
    },

    debugFontChange(action) {
      // Feedback visual temporário
      this.fontSizeChanged = true;
      setTimeout(() => {
        this.fontSizeChanged = false;
      }, 1000);

      // Debug apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.log(`🔤 Fonte ${action}:`, {
          fontSize: this.fontSize,
          scale: this.fontSize / 100,
          cssVariable: getComputedStyle(document.documentElement).getPropertyValue('--accessibility-font-scale'),
          hasClass: document.body.classList.contains('accessibility-font-active'),
          hasLargeClass: document.body.classList.contains('accessibility-large-font')
        });
      }
    },

    applyFontSize() {
      // Remove classes anteriores
      document.body.classList.remove('accessibility-font-active', 'accessibility-large-font');

      // IMPORTANTE: Só aplica estilos se a fonte for diferente de 100%
      if (this.fontSize !== 100) {
        const scale = this.fontSize / 100;
        document.documentElement.style.setProperty('--accessibility-font-scale', scale);
        document.body.classList.add('accessibility-font-active');

        // Compatibilidade com classe legada
        if (this.fontSize > 100) {
          document.body.classList.add('accessibility-large-font');
        }
      } else {
        // Remove a variável CSS quando volta ao padrão
        document.documentElement.style.removeProperty('--accessibility-font-scale');
      }
    },

    // ========================================
    // CONTROLE DE CONTRASTE
    // ========================================
    toggleHighContrast() {
      this.isHighContrast = !this.isHighContrast;
      this.applyHighContrast();
      this.saveSettings();
      this.showStatus(
        this.isHighContrast
          ? 'Alto contraste ativado'
          : 'Alto contraste desativado'
      );
    },

    applyHighContrast() {
      document.body.classList.toggle('accessibility-high-contrast', this.isHighContrast);
    },

    // ========================================
    // CONTROLES DE LEITURA (TEXT-TO-SPEECH)
    // ========================================
    toggleTextToSpeech() {
      this.isTextToSpeechEnabled = !this.isTextToSpeechEnabled;
      this.saveSettings();

      if (this.isTextToSpeechEnabled) {
        this.enableTextToSpeech();
        this.showStatus('Narração de texto ativada');
      } else {
        this.disableTextToSpeech();
        this.showStatus('Narração de texto desativada');
      }
    },

    initializeSpeechSynthesis() {
      if ('speechSynthesis' in window) {
        this.speechSynthesis = window.speechSynthesis;
      }
    },

    enableTextToSpeech() {
      if (!this.speechSynthesis) return;

      // Adiciona listeners para leitura de elementos quando focados
      document.addEventListener('focusin', this.handleFocusForSpeech);
      document.addEventListener('mouseenter', this.handleMouseEnterForSpeech, true);
    },

    disableTextToSpeech() {
      document.removeEventListener('focusin', this.handleFocusForSpeech);
      document.removeEventListener('mouseenter', this.handleMouseEnterForSpeech, true);
      this.stopSpeech();
    },

    handleFocusForSpeech(event) {
      if (this.isTextToSpeechEnabled) {
        this.speakElement(event.target);
      }
    },

    handleMouseEnterForSpeech(event) {
      if (this.isTextToSpeechEnabled && event.target.tagName === 'BUTTON') {
        this.speakElement(event.target);
      }
    },

    speakElement(element) {
      if (!this.speechSynthesis || !element) return;

      let textToSpeak = '';

      // Prioridade para aria-label
      if (element.getAttribute('aria-label')) {
        textToSpeak = element.getAttribute('aria-label');
      }
      // Depois título
      else if (element.title) {
        textToSpeak = element.title;
      }
      // Depois texto visível
      else if (element.textContent && element.textContent.trim()) {
        textToSpeak = element.textContent.trim();
      }
      // Para inputs, ler o label associado
      else if (element.tagName === 'INPUT') {
        const label = document.querySelector(`label[for="${element.id}"]`);
        if (label) {
          textToSpeak = label.textContent.trim();
        }
      }

      if (textToSpeak && textToSpeak.length > 0 && textToSpeak.length < 200) {
        this.speak(textToSpeak);
      }
    },

    speak(text) {
      if (!this.speechSynthesis) return;

      // Para qualquer fala anterior
      this.stopSpeech();

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'pt-BR';
      utterance.rate = 0.8;
      utterance.pitch = 1;
      utterance.volume = 0.8;

      this.currentSpeech = utterance;
      this.speechSynthesis.speak(utterance);
    },

    stopSpeech() {
      if (this.speechSynthesis) {
        this.speechSynthesis.cancel();
      }
      this.currentSpeech = null;
    },

    readPageContent() {
      if (!this.speechSynthesis) return;

      // Coleta o conteúdo principal da página
      const mainContent = document.querySelector('main, [role="main"], .main-content, #app');
      if (mainContent) {
        let textContent = this.extractReadableText(mainContent);
        // Limita a 1000 caracteres para não ser muito longo
        if (textContent.length > 1000) {
          textContent = textContent.substring(0, 1000) + '...';
        }
        this.speak(textContent);
        this.showStatus('Lendo conteúdo da página...');
      }
    },

    extractReadableText(element) {
      // Remove scripts e estilos
      const clone = element.cloneNode(true);
      const scriptsAndStyles = clone.querySelectorAll('script, style, .accessibility-widget');
      scriptsAndStyles.forEach(el => el.remove());

      return clone.textContent || clone.innerText || '';
    },

    // ========================================
    // NAVEGAÇÃO APRIMORADA POR TECLADO
    // ========================================
    toggleKeyboardNavigation() {
      this.isKeyboardNavigationEnhanced = !this.isKeyboardNavigationEnhanced;
      this.applyKeyboardNavigation();
      this.saveSettings();
      this.showStatus(
        this.isKeyboardNavigationEnhanced
          ? 'Navegação aprimorada ativada'
          : 'Navegação aprimorada desativada'
      );
    },

    applyKeyboardNavigation() {
      document.body.classList.toggle('accessibility-enhanced-keyboard', this.isKeyboardNavigationEnhanced);
    },

    setupKeyboardListeners() {
      document.addEventListener('keydown', this.handleGlobalKeydown);
    },

    removeKeyboardListeners() {
      document.removeEventListener('keydown', this.handleGlobalKeydown);
    },

    handleGlobalKeydown(event) {
      // ESC para fechar painel de acessibilidade
      if (event.key === 'Escape' && this.isPanelOpen) {
        this.closePanel();
        return;
      }

      // Atalhos de acessibilidade (Alt + tecla)
      if (event.altKey) {
        switch (event.key) {
          case 'a':
          case 'A':
            event.preventDefault();
            this.togglePanel();
            break;
          case '+':
          case '=':
            event.preventDefault();
            this.increaseFontSize();
            break;
          case '-':
            event.preventDefault();
            this.decreaseFontSize();
            break;
          case 'c':
          case 'C':
            event.preventDefault();
            this.toggleHighContrast();
            break;
          case 's':
          case 'S':
            event.preventDefault();
            this.toggleTextToSpeech();
            break;
        }
      }
    },

    // ========================================
    // MODO DE LEITURA
    // ========================================
    toggleReadingMode() {
      this.isReadingMode = !this.isReadingMode;
      this.applyReadingMode();
      this.saveSettings();
      this.showStatus(
        this.isReadingMode
          ? 'Modo de leitura ativado'
          : 'Modo de leitura desativado'
      );
    },

    applyReadingMode() {
      document.body.classList.toggle('accessibility-reading-mode', this.isReadingMode);
    },

    // ========================================
    // GERENCIAMENTO DE CONFIGURAÇÕES
    // ========================================
    saveSettings() {
      const settings = {
        fontSize: this.fontSize,
        isHighContrast: this.isHighContrast,
        isTextToSpeechEnabled: this.isTextToSpeechEnabled,
        isKeyboardNavigationEnhanced: this.isKeyboardNavigationEnhanced,
        isReadingMode: this.isReadingMode
      };

      localStorage.setItem('accessibility-settings', JSON.stringify(settings));
    },

    loadAccessibilitySettings() {
      try {
        const saved = localStorage.getItem('accessibility-settings');
        if (saved) {
          const settings = JSON.parse(saved);
          this.fontSize = settings.fontSize || 100;
          this.isHighContrast = settings.isHighContrast || false;
          this.isTextToSpeechEnabled = settings.isTextToSpeechEnabled || false;
          this.isKeyboardNavigationEnhanced = settings.isKeyboardNavigationEnhanced || false;
          this.isReadingMode = settings.isReadingMode || false;
        }
      } catch (error) {
        console.warn('Erro ao carregar configurações de acessibilidade:', error);
      }
    },

    applyAllSettings() {
      // Só aplica configurações que estão diferentes do padrão
      this.applyFontSize();

      if (this.isHighContrast) {
        this.applyHighContrast();
      }

      if (this.isKeyboardNavigationEnhanced) {
        this.applyKeyboardNavigation();
      }

      if (this.isReadingMode) {
        this.applyReadingMode();
      }

      if (this.isTextToSpeechEnabled) {
        this.enableTextToSpeech();
      }
    },

    resetAccessibilitySettings() {
      this.fontSize = 100;
      this.isHighContrast = false;
      this.isTextToSpeechEnabled = false;
      this.isKeyboardNavigationEnhanced = false;
      this.isReadingMode = false;

      // Remove todas as classes relacionadas
      document.body.classList.remove(
        'accessibility-font-active',
        'accessibility-large-font',
        'accessibility-high-contrast',
        'accessibility-enhanced-keyboard',
        'accessibility-reading-mode'
      );

      // Reset da variável CSS
      document.documentElement.style.removeProperty('--accessibility-font-scale');

      this.applyAllSettings();
      this.disableTextToSpeech();
      this.saveSettings();
      this.showStatus('Configurações restauradas para o padrão');
    },

    // ========================================
    // UTILITÁRIOS
    // ========================================
    showStatus(message) {
      this.statusMessage = message;
      setTimeout(() => {
        this.statusMessage = '';
      }, 3000);
    },

    // Helpers para cores (similares ao componente pai)
    lightenColor(color, amount) {
      if (color.startsWith('#')) {
        const num = parseInt(color.replace('#', ''), 16);
        const amt = Math.round(2.55 * amount * 100);
        const R = (num >> 16) + amt;
        const G = (num >> 8 & 0x00FF) + amt;
        const B = (num & 0x0000FF) + amt;
        return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
          (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
          (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
      }
      return color;
    },

    darkenColor(color, amount) {
      if (color.startsWith('#')) {
        const num = parseInt(color.replace('#', ''), 16);
        const amt = Math.round(2.55 * amount * 100);
        const R = (num >> 16) - amt;
        const G = (num >> 8 & 0x00FF) - amt;
        const B = (num & 0x0000FF) - amt;
        return '#' + (0x1000000 + (R > 255 ? 255 : R < 0 ? 0 : R) * 0x10000 +
          (G > 255 ? 255 : G < 0 ? 0 : G) * 0x100 +
          (B > 255 ? 255 : B < 0 ? 0 : B)).toString(16).slice(1);
      }
      return color;
    }
  }
}
</script>

<style lang="scss" scoped>
.accessibility-widget {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.accessibility-toggle-btn {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: var(--primary-color, #156440);
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  &:hover, &:focus {
    background: var(--primary-color-light, #1a704a);
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    outline: 3px solid rgba(255, 255, 255, 0.5);
  }

  &:active {
    background: var(--primary-color-dark, #0d4d35);
    transform: scale(0.95);
  }

  &.active {
    background: var(--primary-color-dark, #0d4d35);

    &:hover {
      background: var(--primary-color, #156440);
    }
  }

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    font-size: 20px;
  }
}

.accessibility-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: -1;
}

.accessibility-panel {
  position: absolute;
  top: 70px;
  right: 0;
  width: 320px;
  max-height: 80vh;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  border: 1px solid #e0e0e0;

  @media (max-width: 768px) {
    width: 280px;
    top: 60px;
    max-height: 70vh;
  }

  @media (max-width: 480px) {
    width: calc(100vw - 30px);
    right: -15px;
  }
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: var(--primary-color, #156640);
  color: white;
}

.panel-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;

  &:hover, &:focus {
    background: rgba(255, 255, 255, 0.2);
    outline: 2px solid rgba(255, 255, 255, 0.5);
  }
}

.panel-content {
  padding: 20px;
  max-height: calc(80vh - 60px);
  overflow-y: auto;
}

.control-group {
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
}

.control-title {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
}

.control-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.control-btn {
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;

  &:hover, &:focus {
    background: #e9e9e9;
    border-color: var(--primary-color, #156440);
    outline: 2px solid rgba(21, 100, 64, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      background: #f5f5f5;
      border-color: #ddd;
    }
  }

  &.toggle-btn.active {
    background: var(--primary-color, #156440);
    color: white;
    border-color: var(--primary-color, #156440);

    &:hover {
      background: var(--primary-color-dark, #0d4d35);
    }
  }

  &.reset-btn {
    background: #ff6b6b;
    color: white;
    border-color: #ff6b6b;
    width: 100%;
    justify-content: center;

    &:hover, &:focus {
      background: #ff5252;
      border-color: #ff5252;
    }
  }
}

.font-size-display {
  font-size: 12px;
  font-weight: 600;
  color: var(--primary-color, #156440);
  min-width: 40px;
  text-align: center;
  transition: all 0.3s ease;

  &.font-size-changed {
    background: var(--primary-color, #156440);
    color: white;
    padding: 2px 6px;
    border-radius: 4px;
    transform: scale(1.1);
  }
}

.shortcuts-info {
  background: #f8f9fa;
  padding: 8px 12px;
  border-radius: 6px;
  border-left: 3px solid var(--primary-color, #156440);

  small {
    line-height: 1.4;
    color: #666;
  }
}

.status-message {
  background: #e8f5e8;
  color: #2d5f2d;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  margin-top: 16px;
  border-left: 3px solid var(--primary-color, #156440);
}

// Animações
.slide-up-enter-active, .slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter, .slide-up-leave-to {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}
</style>

<!-- Estilos globais para acessibilidade - APLICADOS SOMENTE QUANDO ACESSIBILIDADE ESTÁ ATIVA -->
<style lang="scss">
/*
   IMPORTANTE: Estes estilos só são aplicados quando as classes de acessibilidade estão ativas
   Eles NÃO interferem com o comportamento padrão da aplicação
*/

// Escala de fonte - APLICADA SOMENTE QUANDO ATIVA
body.accessibility-font-active {
  // Elementos de texto principais
  p, span, div, li, td, th, label, button, input, textarea, select,
  h1, h2, h3, h4, h5, h6, .btn, .form-control, .nav-link,
  .card-title, .card-text, .modal-title, .modal-body,
  .alert, .badge, .breadcrumb, .dropdown-item,
  .navbar-nav .nav-link, .sidebar .nav-link, .menu-content .menu-item {
    font-size: calc(1em * var(--accessibility-font-scale, 1)) !important;
  }

  // Elementos menores
  small, .small, .text-muted {
    font-size: calc(0.875em * var(--accessibility-font-scale, 1)) !important;
  }
}

// Alto contraste - APLICADO SOMENTE QUANDO ATIVO
body.accessibility-high-contrast {
  filter: contrast(150%) brightness(110%);

  * {
    text-shadow: none !important;
    box-shadow: none !important;
  }

  a, button {
    text-decoration: underline !important;
  }

  input, textarea, select {
    border: 2px solid #000 !important;
    background: #fff !important;
    color: #000 !important;
  }
}

// Navegação por teclado - APLICADA SOMENTE QUANDO ATIVA
body.accessibility-enhanced-keyboard {
  *:focus {
    outline: 3px solid #4A90E2 !important;
    outline-offset: 2px !important;
    z-index: 1000;
    position: relative;
  }

  button:focus, a:focus, input:focus, textarea:focus, select:focus {
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.3) !important;
    transform: scale(1.02);
    transition: transform 0.2s ease;
  }

  // Melhora elementos interativos
  button, a, input, select, textarea {
    &:hover {
      cursor: pointer;
      transform: scale(1.02);
      transition: transform 0.2s ease;
    }
  }
}

// Modo de leitura - APLICADO SOMENTE QUANDO ATIVO
body.accessibility-reading-mode {
  // Oculta elementos decorativos não essenciais
  .bkg_img, .decoration, .advertisement,
  .sidebar:not(.accessibility-keep) {
    display: none !important;
  }

  // Simplifica layout para leitura
  .main-content, .container, .container-fluid {
    max-width: 800px !important;
    margin: 0 auto !important;
    padding: 20px !important;
  }

  // Melhora legibilidade
  p, li, span, div {
    line-height: 1.6 !important;
    color: #333 !important;
  }

  // Simplifica elementos visuais
  .card, .box, .panel {
    border: 1px solid #ddd !important;
    border-radius: 4px !important;
    background: #fff !important;
    box-shadow: none !important;
  }
}

// Utilitário para leitores de tela (sempre disponível)
.sr-only {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

// Responsividade - apenas quando acessibilidade está ativa
@media (max-width: 768px) {
  body.accessibility-font-active {
    .accessibility-widget {
      top: 10px;
      right: 10px;
    }
  }
}

// Suporte a preferências do sistema - apenas quando alto contraste está ativo
@media (prefers-color-scheme: dark) {
  body.accessibility-high-contrast {
    filter: contrast(130%) brightness(90%) invert(1) hue-rotate(180deg);
  }
}

// Redução de movimento - aplicado quando a classe estiver ativa
body.accessibility-reduced-motion {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}
</style>
